import type { CSSProperties } from 'react';
import type { ColorScheme } from '../utils/colorScheme';
import type { ShellLayoutId } from './shellLayouts/shellLayoutTypes';
import { shellLayoutForConceptIndex } from './shellLayouts/shellLayoutTypes';

/** Visual family — shifts saturation / contrast curves for each concept. */
export type UiTone = 'warm' | 'cool' | 'neon' | 'earth' | 'pastel' | 'mono';

export interface ConceptMeta {
  readonly id: string;
  readonly en: string;
  readonly ar: string;
  readonly hue: number;
  readonly tone: UiTone;
}

const QUICK_POOL = [
  'data-table',
  'first-glance',
  'options-flow',
  'detailed-options-charts',
  'volume-oi-spot-gamma',
  'vertical-spot-gamma-oi',
  'gex-chart',
  'flow-heatmap',
  'elite-pulse-report',
  'gamma-credit-hunter',
] as const;

function quickTabIdsForIndex(i: number): string[] {
  const pool = [...QUICK_POOL];
  const start = i % pool.length;
  return [...pool.slice(start), ...pool.slice(0, start)].slice(0, 8);
}

/** Exactly 30 non-legacy concepts — each gets distinct palette + shell + quick-tab order. */
export const CONCEPT_META: readonly ConceptMeta[] = [
  { id: 'warm-gcc', en: 'Warm GCC', ar: 'خليجي دافئ', hue: 28, tone: 'warm' },
  { id: 'soft-pro', en: 'Soft Pro', ar: 'احترافي ناعم', hue: 218, tone: 'cool' },
  { id: 'dense-terminal', en: 'Dense terminal', ar: 'طرفية كثيفة', hue: 95, tone: 'mono' },
  { id: 'nordic-frost', en: 'Nordic frost', ar: 'صقيع شمالي', hue: 202, tone: 'cool' },
  { id: 'tokyo-night', en: 'Tokyo night', ar: 'ليل طوكيو', hue: 258, tone: 'neon' },
  { id: 'saharan-dawn', en: 'Saharan dawn', ar: 'فجر صحراوي', hue: 32, tone: 'earth' },
  { id: 'cobalt-trade', en: 'Cobalt trade', ar: 'تداول كوبالت', hue: 224, tone: 'cool' },
  { id: 'emerald-bourse', en: 'Emerald bourse', ar: 'بورصة زمردية', hue: 158, tone: 'earth' },
  { id: 'amethyst-desk', en: 'Amethyst desk', ar: 'مكتب جمشتي', hue: 283, tone: 'pastel' },
  { id: 'coral-terminal', en: 'Coral terminal', ar: 'مرجانية', hue: 12, tone: 'warm' },
  { id: 'graphite-lite', en: 'Graphite lite', ar: 'غرافيت خفيف', hue: 220, tone: 'mono' },
  { id: 'amber-ledger', en: 'Amber ledger', ar: 'دفتر كهرماني', hue: 38, tone: 'warm' },
  { id: 'mint-matrix', en: 'Mint matrix', ar: 'مصفوفة نعناع', hue: 168, tone: 'pastel' },
  { id: 'rose-analytics', en: 'Rose analytics', ar: 'تحليلات وردية', hue: 333, tone: 'pastel' },
  { id: 'slate-command', en: 'Slate command', ar: 'أردوازي قيادي', hue: 208, tone: 'cool' },
  { id: 'obsidian-vega', en: 'Obsidian vega', ar: 'سبجي', hue: 268, tone: 'mono' },
  { id: 'arctic-flow', en: 'Arctic flow', ar: 'تدفق قطبي', hue: 196, tone: 'cool' },
  { id: 'ember-trade', en: 'Ember trade', ar: 'تداول جمر', hue: 18, tone: 'warm' },
  { id: 'violet-surge', en: 'Violet surge', ar: 'اندفاع بنفسجي', hue: 275, tone: 'neon' },
  { id: 'lime-pit', en: 'Lime pit', ar: 'حفرة ليمونية', hue: 88, tone: 'neon' },
  { id: 'cherry-board', en: 'Cherry board', ar: 'لوحة كرز', hue: 350, tone: 'warm' },
  { id: 'midnight-forge', en: 'Midnight forge', ar: 'حدادة ليلية', hue: 238, tone: 'cool' },
  { id: 'glacier-io', en: 'Glacier I/O', ar: 'نحات جليدي', hue: 188, tone: 'pastel' },
  { id: 'sunlit-desk', en: 'Sunlit desk', ar: 'مكتب مشمس', hue: 48, tone: 'warm' },
  { id: 'petrol-flow', en: 'Petrol flow', ar: 'تدفق بنزيني', hue: 198, tone: 'earth' },
  { id: 'wine-analytics', en: 'Wine analytics', ar: 'تحليلات نبيذية', hue: 340, tone: 'earth' },
  { id: 'honey-ledger', en: 'Honey ledger', ar: 'دفتر عسلي', hue: 42, tone: 'warm' },
  { id: 'neon-drift', en: 'Neon drift', ar: 'انجراف نيون', hue: 312, tone: 'neon' },
  { id: 'ocean-arc', en: 'Ocean arc', ar: 'قوس محيطي', hue: 202, tone: 'cool' },
  { id: 'dune-study', en: 'Dune study', ar: 'دراسة كثبان', hue: 36, tone: 'earth' },
] as const;

export const DEFAULT_UI_CONCEPT_ID = CONCEPT_META[0].id;

export type NonLegacyConceptId = (typeof CONCEPT_META)[number]['id'];

export type UiConceptId = NonLegacyConceptId | 'legacy';

export const UI_CONCEPT_IDS: NonLegacyConceptId[] = CONCEPT_META.map((m) => m.id as NonLegacyConceptId);

export const UI_CONCEPT_LABELS: Record<UiConceptId, { en: string; ar: string }> = {
  ...Object.fromEntries(
    CONCEPT_META.map((m) => [m.id, { en: m.en, ar: m.ar }] as const)
  ) as Record<NonLegacyConceptId, { en: string; ar: string }>,
  legacy: { en: 'Legacy (classic UI)', ar: 'الواجهة الكلاسيكية' },
};

export function isNonLegacyConceptId(s: string): s is NonLegacyConceptId {
  return (UI_CONCEPT_IDS as readonly string[]).includes(s);
}

export function isUiConceptId(s: string): s is UiConceptId {
  return s === 'legacy' || isNonLegacyConceptId(s);
}

function H(h: number, s: number, l: number): string {
  return `hsl(${Math.round(h)} ${Math.round(s)}% ${Math.round(l)}%)`;
}

function buildSchemePartial(meta: ConceptMeta, dark: boolean): Partial<ColorScheme> {
  const { hue, tone } = meta;
  const satBoost = tone === 'neon' ? 22 : tone === 'pastel' ? -18 : tone === 'mono' ? -28 : 0;
  const s1 = Math.min(92, 58 + satBoost);
  const s2 = Math.min(88, 50 + satBoost);
  const sGrid = Math.max(8, 22 + satBoost * 0.4);

  if (dark) {
    const bgL = tone === 'neon' ? 6 : tone === 'pastel' ? 11 : 9;
    const surfL = bgL + 4;
    return {
      primary: H((hue + 198) % 360, s1, 52),
      secondary: H((hue + 72) % 360, s2, 50),
      accent: H((hue + 312) % 360, Math.min(90, s1 + 8), 58),
      success: H(152, Math.min(85, 55 + satBoost * 0.5), 48),
      warning: H(38, 82, 52),
      error: H(0, 72, 55),
      info: H((hue + 205) % 360, s1, 54),
      background: H(hue, tone === 'mono' ? 8 : 26, bgL),
      surface: H(hue, tone === 'mono' ? 6 : 22, surfL),
      surfaceHover: H(hue, tone === 'mono' ? 10 : 24, surfL + 6),
      surfaceBorder: H(hue, tone === 'mono' ? 12 : 18, surfL + 14),
      text: tone === 'mono' ? H(0, 0, 96) : H((hue + 50) % 360, 12, 94),
      textSecondary: H(hue, tone === 'mono' ? 5 : 14, 78),
      textMuted: H(hue, tone === 'mono' ? 6 : 12, 62),
      textInverse: H(hue, 35, 12),
      chartPrimary: H(hue, Math.min(92, s1 + 12), 58),
      chartSecondary: H((hue + 58) % 360, s2, 54),
      chartSuccess: H(145, 62, 50),
      chartWarning: H(45, 78, 55),
      chartError: H(2, 70, 58),
      boughtCalls: H(142 + (hue % 7 - 3), 62, 48),
      soldCalls: H((hue + 262) % 360, 58, 58),
      boughtPuts: H((hue * 3 + 4) % 360, 68, 56),
      soldPuts: H(32, 80, 56),
      sentimentPositive: H(142, 60, 50),
      sentimentNegative: H(0, 68, 55),
      sentimentNeutral: H(hue, sGrid, 48),
    };
  }

  return {
    primary: H((hue + 200) % 360, Math.min(85, s1 + 5), 38),
    secondary: H((hue + 70) % 360, s2, 40),
    accent: H((hue + 300) % 360, 70, 45),
    success: H(152, 55, 36),
    warning: H(32, 85, 44),
    error: H(0, 72, 42),
    info: H((hue + 200) % 360, 65, 42),
    background: H(hue, tone === 'pastel' ? 35 : 28, 98),
    surface: H(hue, 22, 100),
    surfaceHover: H(hue, 18, 96),
    surfaceBorder: H(hue, 16, 86),
    text: H(hue, 28, 14),
    textSecondary: H(hue, 18, 32),
    textMuted: H(hue, 12, 46),
    textInverse: H(0, 0, 100),
    chartPrimary: H(hue, Math.min(88, s1 + 5), 42),
    chartSecondary: H((hue + 55) % 360, s2, 40),
    chartSuccess: H(145, 55, 36),
    chartWarning: H(38, 85, 44),
    chartError: H(355, 72, 44),
    boughtCalls: H(138, 55, 34),
    soldCalls: H((hue + 268) % 360, 52, 42),
    boughtPuts: H(0, 65, 42),
    soldPuts: H(28, 78, 44),
    sentimentPositive: H(142, 55, 36),
    sentimentNegative: H(0, 68, 42),
    sentimentNeutral: H(hue, 14, 52),
  };
}

export interface ConceptDefinition {
  readonly meta: ConceptMeta;
  readonly colorsDark: Partial<ColorScheme>;
  readonly colorsLight: Partial<ColorScheme>;
  readonly quickTabIds: readonly string[];
  /** Structural shell: different navigation & spatial model per layout id. */
  readonly shellLayoutId: ShellLayoutId;
  /** Shell layout tokens (independent of light/dark chart scheme) */
  readonly shellPad: number;
  readonly shellRadius: number;
  readonly shellRail: number;
  readonly shellFont: string;
  readonly density: 'comfortable' | 'compact' | 'terminal';
}

function buildDefinition(meta: ConceptMeta, index: number): ConceptDefinition {
  return {
    meta,
    colorsDark: buildSchemePartial(meta, true),
    colorsLight: buildSchemePartial(meta, false),
    quickTabIds: quickTabIdsForIndex(index),
    shellLayoutId: shellLayoutForConceptIndex(index),
    shellPad: meta.id === 'dense-terminal' ? 10 : meta.tone === 'pastel' ? 22 : 18,
    shellRadius: meta.tone === 'pastel' ? 16 : meta.tone === 'neon' ? 8 : meta.tone === 'mono' ? 6 : 12,
    shellRail:
      meta.id === 'dense-terminal'
        ? 216
        : meta.tone === 'pastel'
          ? 288
          : 252,
    shellFont:
      meta.tone === 'mono' || meta.id === 'dense-terminal'
        ? 'ui-monospace, "Cascadia Code", Consolas, monospace'
        : meta.tone === 'pastel'
          ? '"Nunito", system-ui, sans-serif'
          : meta.tone === 'warm'
            ? '"IBM Plex Sans", system-ui, sans-serif'
            : 'Inter, system-ui, sans-serif',
    density: meta.id === 'dense-terminal' || meta.tone === 'mono' ? 'terminal' : meta.tone === 'pastel' ? 'comfortable' : 'compact',
  };
}

export const CONCEPT_REGISTRY: Record<NonLegacyConceptId, ConceptDefinition> = Object.fromEntries(
  CONCEPT_META.map((m, i) => [m.id, buildDefinition(m, i)])
) as Record<NonLegacyConceptId, ConceptDefinition>;

export function mergeUiConceptIntoScheme(
  conceptId: UiConceptId,
  isDarkMode: boolean,
  base: ColorScheme
): ColorScheme {
  if (conceptId === 'legacy') return base;
  const def = CONCEPT_REGISTRY[conceptId];
  if (!def) return base;
  const overlay = isDarkMode ? def.colorsDark : def.colorsLight;
  return { ...base, ...overlay };
}

/** CSS vars for `.concept-shell` (page chrome). */
export function getShellCssVariables(conceptId: NonLegacyConceptId, isDarkMode: boolean): CSSProperties {
  const def = CONCEPT_REGISTRY[conceptId];
  const c = isDarkMode ? def.colorsDark : def.colorsLight;
  const page = c.background ?? '#0f172a';
  const surface = c.surface ?? '#1e293b';
  const border = c.surfaceBorder ?? '#475569';
  const ink = c.text ?? '#f8fafc';
  const muted = c.textMuted ?? '#94a3b8';
  const accent = c.chartPrimary ?? '#3b82f6';
  const card = isDarkMode ? (c.surfaceHover ?? surface) : '#ffffff';

  return {
    '--shell-page': page,
    '--shell-surface': surface,
    '--shell-card': card,
    '--shell-border': border,
    '--shell-ink': ink,
    '--shell-muted': muted,
    '--shell-accent': accent,
    '--shell-pad': `${def.shellPad}px`,
    '--shell-radius': `${def.shellRadius}px`,
    '--shell-rail': `${def.shellRail}px`,
    '--shell-font': def.shellFont,
  } as CSSProperties;
}

export function getQuickTabsForConcept<T extends { id: string }>(
  mainTabs: T[],
  conceptId: NonLegacyConceptId
): T[] {
  const order = CONCEPT_REGISTRY[conceptId]?.quickTabIds ?? [];
  if (!order.length) return mainTabs.slice(0, 10);
  const map = new Map(mainTabs.map((t) => [t.id, t]));
  const picked: T[] = [];
  for (const id of order) {
    const hit = map.get(id);
    if (hit) picked.push(hit);
  }
  for (const t of mainTabs) {
    if (!picked.includes(t)) picked.push(t);
  }
  return picked.slice(0, 12);
}
